// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Roman Morawek

/* 
Unfortunately, react-router v6 does not support class components, see
https://stackoverflow.com/questions/62365009/how-to-get-parameter-value-from-react-router-dom-v6-in-class
Thus, we need to use hooks to get the router information.

We also use this to dispatch routing information to the redux store.
*/

import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { NavigateFunction, Location } from "react-router";
import { useBlocker } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { DataState, MainPanel, RootPath } from "types";
import { stringEnumToLabels } from "guiutils";
import { IStateRouter } from "states/routerstates";
import { ActionType, IActionTypeConfirmPopup, raiseAction } from "actions";
import { IState } from "states/states";

export interface RoutedProps {
  navigate: NavigateFunction;
}

function parseUrl(location: Location) {
  let root: RootPath | undefined;
  let panel: MainPanel | undefined;
  // get parts and remove empty start and potentially end
  const parts = location.pathname.split("/").filter((curItem) => Boolean(curItem));
  if (parts.length === 2) {
    // that's the easy case: we face a root and a panel
    root = parts[0] as RootPath;
    panel = parts[1] as MainPanel;
  } else if (parts.length === 1) {
    const path = parts[0];
    const isRootLabel = stringEnumToLabels(RootPath).includes(path as RootPath);
    const isPanelLabel = stringEnumToLabels(MainPanel).includes(path as MainPanel);
    if (isRootLabel && !isPanelLabel) {
      root = path as RootPath;
    } else if (!isRootLabel && isPanelLabel) {
      panel = path as MainPanel;
    } else {
      console.error("invalid url path", path);
    }
  } else if (parts.length === 0) {
    // that's OK, we got the index url
  } else {
    console.error("illegal path", parts);
  }
  return { root, panel };
}

export function withRouteRouter<P extends RoutedProps>(Child: React.ComponentClass<P>) {
  const WithRouterComp = (props: Omit<P, keyof RoutedProps>) => {
    const location = useLocation();
    const dispatch = useDispatch();
    const saveState = useSelector((state: IState) => state.scheduler.saveState);

    useBlocker(({ currentLocation, nextLocation }) => {
      // check if there are unsaved changes...
      const isBlocked =
        saveState === DataState.DIRTY && nextLocation.state?.resetUnsavedChanges !== true;
      if (isBlocked) {
        // show save confirmation window
        console.log("blocking location change to", nextLocation);
        const payload: IActionTypeConfirmPopup = {
          title: undefined,
          message: "",
          location: nextLocation,
        };
        dispatch(raiseAction(ActionType.ACT_CONFIRM_POPUP, payload));
      }
      return isBlocked;
    });

    useEffect(() => {
      // execute on location change
      const { root, panel } = parseUrl(location);
      const params = new URLSearchParams(location.search);
      const payload: IStateRouter = {
        rootPath: root,
        panel,
        widget: params.has("widget") ? parseInt(params.get("widget")!) : undefined,
        charger: params.has("charger") ? parseInt(params.get("charger")!) : undefined,
        connector: params.has("connector") ? parseInt(params.get("connector")!) : undefined,
        transaction: params.has("transaction") ? parseInt(params.get("transaction")!) : undefined,
        rfid: params.has("rfid") ? parseInt(params.get("rfid")!) : undefined,
        report: params.has("report") ? parseInt(params.get("report")!) : undefined,
        user: params.has("user") ? parseInt(params.get("user")!) : undefined,
      };
      dispatch(raiseAction(ActionType.ACT_NAVIGATION, payload));
    }, [location, dispatch]); // only execute on pathname change
    // provide navigate function to child components
    const navigate = useNavigate();
    return <Child {...(props as P)} navigate={navigate} />;
  };
  return WithRouterComp;
}

export function withRouter<P extends RoutedProps>(Child: React.ComponentClass<P>) {
  const WithRouterComp = (props: Omit<P, keyof RoutedProps>) => {
    const navigate = useNavigate();
    return <Child {...(props as P)} navigate={navigate} />;
  };
  return WithRouterComp;
}
